/**
 * Wizard interoperabilità RENTRI ( Impostazioni — senza conservazione).
 * Step 1: P12 + password · Step 2: conferma num_iscr · Step 3: test connessione mTLS
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Shield,
  CheckCircle2,
  XCircle,
  Loader2,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import { apiErrorMessage, endpoints, type CertificateItem } from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input, Label } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { CenteredLoader, ErrorState } from "@/components/ui/feedback";
import { bootstrapOperatoreContext } from "@/lib/bootstrapOperatore";
import { useOperatoreContext } from "@/lib/operatoreContext";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/utils";

const WIZARD_DONE_KEY = "rentri.p12WizardCompleted";

const STEPS = [
  { title: "Certificato", hint: "File .p12 e password" },
  { title: "Operatore", hint: "CF/P.IVA + num_iscr" },
  { title: "Connessione", hint: "Verifica RENTRI" },
] as const;

export function isP12WizardCompleted(): boolean {
  return localStorage.getItem(WIZARD_DONE_KEY) === "1";
}

export function P12SetupWizard({ defaultExpanded }: { defaultExpanded?: boolean }) {
  const qc = useQueryClient();
  const ctx = useOperatoreContext();
  const [expanded, setExpanded] = useState(defaultExpanded ?? !isP12WizardCompleted());
  const [step, setStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState("");
  const [label, setLabel] = useState("Certificato RENTRI");
  const [uploaded, setUploaded] = useState<CertificateItem | null>(null);
  const [cfPiva, setCfPiva] = useState("");
  const [numIscr, setNumIscr] = useState("");
  const [step0Hint, setStep0Hint] = useState<string | null>(null);

  const certs = useQuery({
    queryKey: ["certs"],
    queryFn: endpoints.certificates.list,
  });
  const rentriHealth = useQuery({
    queryKey: ["health-rentri-wizard"],
    queryFn: endpoints.rentriHealth,
    enabled: expanded && step === 2,
    refetchInterval: step === 2 ? 5_000 : false,
    retry: false,
  });

  const uploadMut = useMutation({
    mutationFn: () => endpoints.certificates.upload(file!, label.trim(), password.trim()),
    onSuccess: async (cert) => {
      setStep0Hint(null);
      setUploaded(cert);
      const cfFromCert = cert.subject_cf?.trim() ?? "";
      if (cfFromCert.length >= 11) {
        setCfPiva(cfFromCert);
        ctx.setCf(cfFromCert);
      }
      const iscr = cert.rentri_iscrizione?.trim() ?? "";
      if (iscr.toUpperCase().startsWith("OP")) setNumIscr(iscr.toUpperCase());
      toast.success("Certificato caricato e cifrato");
      qc.invalidateQueries({ queryKey: ["certs"] });
      qc.invalidateQueries({ queryKey: ["health-rentri"] });
      const cf = cfFromCert || cfPiva.trim() || ctx.cf;
      if (cf.length >= 11) {
        try {
          await bootstrapOperatoreContext(ctx, cf);
        } catch {
          /* RENTRI non raggiungibile: step operatore manuale */
        }
      }
      setStep(1);
    },
    onError: (e: unknown) => {
      const msg = apiErrorMessage(e);
      setStep0Hint(msg);
      toast.error(msg);
    },
  });

  const verifyIscr = useMutation({
    mutationFn: (cf: string) => endpoints.anagrafiche.controlloIscrizione(cf.trim()),
    onSuccess: async (isOk, cf) => {
      if (!isOk) return;
      try {
        const rows = await endpoints.anagrafiche.operatori(cf.trim());
        const op = Array.isArray(rows) ? rows[0] : undefined;
        const found = op && typeof op === "object" ? String((op as { num_iscr?: string }).num_iscr ?? "") : "";
        if (found) {
          setNumIscr(found);
          if (op && typeof op === "object" && (op as { denominazione?: string }).denominazione) {
            ctx.setOperatore(found, String((op as { denominazione?: string }).denominazione));
          }
        }
      } catch {
        /* num_iscr opzionale dopo verifica CF */
      }
    },
  });

  useEffect(() => {
    if (ctx.cf) setCfPiva(ctx.cf);
  }, [ctx.cf]);

  useEffect(() => {
    const saved = certs.data?.[0];
    const iscr =
      uploaded?.rentri_iscrizione?.trim() ||
      saved?.rentri_iscrizione?.trim() ||
      "";
    if (iscr.toUpperCase().startsWith("OP")) setNumIscr(iscr.toUpperCase());
    else if (ctx.numIscr) setNumIscr(ctx.numIscr);
    const cf = uploaded?.subject_cf?.trim() || saved?.subject_cf?.trim() || "";
    if (cf.length >= 11) setCfPiva(cf);
  }, [uploaded, certs.data, ctx.numIscr]);

  const savedCert = certs.data?.[0] ?? null;
  const activeCert = uploaded ?? savedCert;
  const hasSavedCert = (certs.data?.length ?? 0) > 0;

  const finishWizard = async () => {
    if (cfPiva.trim()) ctx.setCf(cfPiva.trim());
    if (numIscr.trim()) {
      ctx.setOperatore(
        numIscr.trim(),
        ctx.denominazione || uploaded?.subject_cn || activeCert?.subject_cn || "Operatore RENTRI",
      );
    }
    try {
      const boot = await bootstrapOperatoreContext(ctx, cfPiva.trim() || ctx.cf);
      if (!boot.ok && boot.missing === "cf") {
        toast.warning("Inserisci CF/P.IVA nel wizard o nella barra laterale per abilitare la sincronizzazione.");
      } else if (!boot.ok && boot.missing === "sito") {
        toast.warning("Nessuna unità locale trovata su RENTRI per questo operatore.");
      }
    } catch {
      /* bootstrap opzionale se RENTRI non raggiungibile */
    }
    localStorage.setItem(WIZARD_DONE_KEY, "1");
    toast.success("Configurazione interoperabilità completata");
    setExpanded(false);
    qc.invalidateQueries({ queryKey: ["health-imp"] });
    qc.invalidateQueries({ queryKey: ["health"] });
    qc.invalidateQueries({ queryKey: ["health-rentri"] });
  };

  const canAdvanceStep0 = file
    ? !!file && label.trim().length > 0 && password.trim().length > 0
    : hasSavedCert;
  const canAdvanceStep1 =
    cfPiva.trim().length >= 11 && numIscr.trim().length >= 8 && verifyIscr.data === true;

  if (!expanded) {
    return (
      <Card>
        <CardContent className="flex flex-col gap-3 py-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <Shield className="mt-0.5 h-8 w-8 text-primary" />
            <div>
              <p className="font-medium">Interoperabilità RENTRI</p>
              <p className="text-sm text-muted-foreground">
                {hasSavedCert
                  ? "Certificato P12 associato al tuo account. Riapri il wizard per sostituirlo o aggiornare l'operatore."
                  : isP12WizardCompleted()
                    ? "Configurazione completata. Riapri il wizard per modificare."
                    : "Dopo l'accesso con Cognito, carica il certificato P12 e la password per collegarti al RENTRI."}
              </p>
            </div>
          </div>
          <Button onClick={() => setExpanded(true)}>Configura certificato</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-primary/20">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="h-5 w-5" /> Configurazione interoperabilità
            </CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Configura certificato, operatore e connessione al portale ministeriale RENTRI
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setExpanded(false)}>
            Chiudi
          </Button>
        </div>
        <div className="mt-4 flex gap-2">
          {STEPS.map((s, i) => (
            <button
              key={s.title}
              type="button"
              onClick={() => i < step && setStep(i)}
              className={cn(
                "flex-1 rounded-md border px-2 py-2 text-left text-xs transition",
                step === i && "border-primary bg-primary/5",
                i < step && "cursor-pointer hover:bg-accent",
                i > step && "opacity-50",
              )}
            >
              <span className="font-semibold">
                {i + 1}. {s.title}
              </span>
              <span className="block text-[10px] text-muted-foreground">{s.hint}</span>
            </button>
          ))}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {step === 0 && (
          <>
            {hasSavedCert && savedCert && (
              <div className="rounded-md border border-primary/20 bg-primary/5 p-3 text-sm">
                <p className="text-xs font-medium text-muted-foreground mb-1">
                  Certificato salvato nel tuo account
                </p>
                <p className="font-medium text-sm">{savedCert.subject_cn ?? savedCert.label}</p>
                <p className="text-xs mt-1 text-muted-foreground">
                  Etichetta: <span className="font-mono">{savedCert.label}</span>
                  {savedCert.subject_cf ? (
                    <>
                      {" · "}
                      CF/P.IVA: <span className="font-mono">{savedCert.subject_cf}</span>
                    </>
                  ) : null}
                  {savedCert.rentri_iscrizione ? (
                    <>
                      {" · "}
                      num_iscr:{" "}
                      <span className="font-mono">{savedCert.rentri_iscrizione}</span>
                    </>
                  ) : null}
                </p>
                <p className="text-[10px] text-muted-foreground mt-2">
                  Agli accessi successivi userai questo certificato (cifrato e legato al tuo utente
                  Cognito). Puoi caricarne uno nuovo qui sotto oppure continuare con quello salvato.
                </p>
              </div>
            )}

            <div className="space-y-3">
              <div>
                <Label>File certificato (.p12 / .pfx)</Label>
                <Input
                  type="file"
                  accept=".p12,.pfx"
                  className="mt-1"
                  onChange={(e) => {
                    setFile(e.target.files?.[0] ?? null);
                    setStep0Hint(null);
                    uploadMut.reset();
                  }}
                />
              </div>
              <div>
                <Label>
                  Password del certificato
                  {file ? <span className="text-destructive"> *</span> : null}
                </Label>
                <Input
                  type="password"
                  className={cn(
                    "mt-1",
                    file && !password.trim() && "border-amber-500 focus-visible:ring-amber-500",
                    step0Hint && "border-destructive focus-visible:ring-destructive",
                  )}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setStep0Hint(null);
                    uploadMut.reset();
                  }}
                  placeholder="Password P12"
                  autoComplete="off"
                />
                {file && !password.trim() && (
                  <p className="mt-1 text-xs text-amber-700">
                    Obbligatoria per decifrare e caricare il file selezionato.
                  </p>
                )}
                {step0Hint && (
                  <p className="mt-1 text-xs text-destructive" role="alert">
                    {step0Hint}
                  </p>
                )}
              </div>
              <div>
                <Label>Etichetta (nome interno)</Label>
                <Input
                  className="mt-1"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Es. Quality & Safety 2026"
                />
              </div>
            </div>

            {hasSavedCert && file && (
              <p className="text-xs text-amber-700">
                Il nuovo file sostituirà il certificato precedente nel tuo account (un solo P12
                attivo).
              </p>
            )}
          </>
        )}

        {step === 1 && (
          <div className="space-y-4">
            {activeCert ? (
              <dl className="grid grid-cols-1 gap-3 rounded-md border p-4 sm:grid-cols-2 text-sm">
                <InfoRow label="Subject CN" value={activeCert.subject_cn} />
                <InfoRow label="Issuer" value={activeCert.issuer_cn} />
                <InfoRow label="Serial" value={activeCert.serial_hex} />
                <InfoRow
                  label="Validità"
                  value={
                    activeCert.expired
                      ? "Scaduto"
                      : `fino al ${formatDate(activeCert.not_after)}`
                  }
                />
              </dl>
            ) : (
              <p className="text-sm text-amber-600">
                Carica un certificato P12 nello step precedente prima di configurare l&apos;operatore.
              </p>
            )}

            <div>
              <Label>Codice fiscale / Partita IVA</Label>
              <Input
                className="mt-1 font-mono"
                value={cfPiva}
                onChange={(e) => {
                  setCfPiva(e.target.value.replace(/\s/g, ""));
                  verifyIscr.reset();
                }}
                placeholder="CF o P.IVA dal certificato"
              />
              <p className="mt-1 text-[10px] text-muted-foreground">
                Per <strong>Verifica iscrizione</strong> RENTRI usa il CF o la P.IVA dell&apos;operatore
                (non il codice <code className="font-mono">OP…</code>).
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={cfPiva.trim().length < 11 || verifyIscr.isPending}
                onClick={() => verifyIscr.mutate(cfPiva.trim())}
              >
                {verifyIscr.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Verifica iscrizione
              </Button>
              {verifyIscr.data !== undefined && (
                <Badge variant={verifyIscr.data ? "success" : "destructive"}>
                  {verifyIscr.data ? "Operatore iscritto" : "Non iscritto / non verificabile"}
                </Badge>
              )}
              {verifyIscr.error && (
                <span className="text-xs text-destructive">Verifica non disponibile in demo</span>
              )}
            </div>

            <div>
              <Label>Numero iscrizione operatore RENTRI (num_iscr)</Label>
              <Input
                className="mt-1 font-mono"
                value={numIscr}
                onChange={(e) => setNumIscr(e.target.value.trim().toUpperCase())}
                placeholder="Es. OP2602VAF081537"
              />
              <p className="mt-1 text-[10px] text-muted-foreground">
                Codice assegnato da RENTRI dopo l&apos;iscrizione. Si compila da solo dopo una
                verifica CF riuscita, oppure dalla pagina <strong>Operatori</strong>.
              </p>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Test di connessione al RENTRI con il certificato configurato (mTLS + token).
            </p>
            {rentriHealth.isLoading && <CenteredLoader />}
            {rentriHealth.error && (
              <ErrorState error={apiErrorMessage(rentriHealth.error)} />
            )}
            {rentriHealth.data && (
              <>
                <div
                  className={cn(
                    "flex items-center gap-3 rounded-md border p-4",
                    rentriHealth.data.status === "ok"
                      ? "border-emerald-500/30 bg-emerald-500/5"
                      : "border-amber-500/30 bg-amber-500/5",
                  )}
                >
                  {rentriHealth.data.status === "ok" ? (
                    <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                  ) : (
                    <XCircle className="h-6 w-6 text-amber-600" />
                  )}
                  <div>
                    <p className="font-medium">
                      {rentriHealth.data.status === "ok"
                        ? "Connessione RENTRI operativa"
                        : "Connessione RENTRI non disponibile"}
                    </p>
                    {rentriHealth.data.status !== "ok" && rentriHealth.data.detail && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {String(rentriHealth.data.detail)}
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void rentriHealth.refetch();
              }}
            >
              Ripeti test
            </Button>
          </div>
        )}

        <div className="flex flex-wrap justify-between gap-2 border-t pt-4">
          <Button
            variant="ghost"
            disabled={step === 0 || uploadMut.isPending}
            onClick={() => setStep((s) => s - 1)}
          >
            <ChevronLeft className="mr-1 h-4 w-4" /> Indietro
          </Button>
          <div className="flex gap-2">
            {step < 2 && (
              <Button
                onClick={() => {
                  if (step === 0) {
                    if (file) {
                      if (!password.trim()) {
                        const msg =
                          "Inserisci la password del certificato P12 prima di continuare.";
                        setStep0Hint(msg);
                        toast.error(msg);
                        return;
                      }
                      if (!label.trim()) {
                        toast.error("Inserisci un'etichetta per il certificato");
                        return;
                      }
                      uploadMut.mutate();
                    } else if (hasSavedCert) {
                      setStep(1);
                    } else {
                      toast.error("Seleziona un file P12 da caricare");
                    }
                  } else if (step === 1) {
                    if (!canAdvanceStep1) {
                      toast.error("Inserisci il numero iscrizione operatore");
                      return;
                    }
                    ctx.setOperatore(
                      numIscr.trim(),
                      activeCert?.subject_cn ?? ctx.denominazione ?? "",
                    );
                    void bootstrapOperatoreContext(ctx, cfPiva.trim() || ctx.cf).finally(() =>
                      setStep(2),
                    );
                  }
                }}
                disabled={
                  (step === 0 && !canAdvanceStep0) || uploadMut.isPending
                }
              >
                {uploadMut.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Caricamento…
                  </>
                ) : (
                  <>
                    Avanti <ChevronRight className="ml-1 h-4 w-4" />
                  </>
                )}
              </Button>
            )}
            {step === 2 && (
              <Button
                onClick={() => {
                  void finishWizard();
                }}
              >
                <CheckCircle2 className="mr-2 h-4 w-4" /> Completa configurazione
              </Button>
            )}
            {step === 0 && hasSavedCert && (
              <Button variant="outline" onClick={() => setStep(1)}>
                Continua con certificato salvato
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function InfoRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <dt className="text-[10px] uppercase text-muted-foreground">{label}</dt>
      <dd className="font-mono text-xs break-all">{value ?? "—"}</dd>
    </div>
  );
}
